import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import Select from "react-select";
import {
  CButton,
  CCard,
  CCardBody,
  CCol,
  CRow,
  CForm,
  CFormInput,
  CFormLabel,
  CFormTextarea,
} from "@coreui/react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import { getSingleTrip, updateTrip } from "src/services/trip.services";
import { getSearchedUser } from "src/services/user.services";
import { getActiveCountryList } from "src/services/country.services";
import { useMutation, useQuery } from "@tanstack/react-query";
import { defaultFormat } from "moment";
import { getSearchAbleRoutes } from "src/services/route.services";

const schema = z.object({
  tripName: z.string().optional(),
  tripId: z.string().min(1, "Required"),
  tripRoute: z.string().min(1, "Required"),
  tripStartTime: z.string().min(1, "Required"),
  tripAssignedBy: z.string().optional(),
  // tripVehicleId: z.string().min(1, "Required"),
  // tripTagId: z.string().optional(),
  tripAssignedTo: z.string().min(1, "Required"),
  tripRemarks: z.string().optional(),
  country: z.string().min(1, "Required"),
});

const UpdateTrip = ({ tripId, setEditTripId, data: allTrips, setData }) => {
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [driverOptions, setDriverOptions] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState("");
  const [driverIdArray, setDriverIdArray] = useState([]);
  const [allDriverData, setAllDriverData] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countryOptions, setCountryOptions] = useState([]);
  const [selectedTripStatus, setSelectedTripStatus] = useState(null);
  const [isFormReady, setIsFormReady] = useState(false);

  const [routeOptions, setRouteOptions] = useState([]);

  const [user, setUser] = useState(
    localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : null
  );

  const {
    data: tripData,
    isLoading: tripLoading,
    isError: tripError,
  } = useQuery({
    queryKey: ["tripData", tripId],
    queryFn: () => getSingleTrip(tripId),
    enabled: !!tripId,
  });

  const TripUpdateMutation = useMutation({
    mutationKey: ["tripUpdate", tripId],
    mutationFn: (input) => updateTrip(input, tripId),
    onMutate: () => {
      toast.loading("Updating trip data...");
    },
    onSuccess: (response) => {
      toast.dismiss();

      if (response.success) {
        const updateTripData = response.data;

        // Find the updated trip in allTrips and update it
        const updatedTrips = allTrips.map((trip) => {
          if (trip._id === updateTripData._id) {
            return {
              ...trip,
              ...updateTripData,
            };
          }
          return trip;
        });

        // Update the data state with the new trips array
        setData(updatedTrips);

        // Close the edit modal
        setEditTripId("");

        toast.success("Trip updated successfully");
        customFormReset();
      }
    },
    onError: (error) => {
      console.log(error);
      toast.dismiss();
      toast.error("Failed to update trip");
    },
  });

  const {
    register,
    setValue,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      tripName: "",
      tripId: "",
      tripRoute: "",
      tripStartTime: "",
      tripAssignedBy: "",
      // tripVehicleId: "",
      tripTagId: "",
      tripAssignedTo: "",
      tripRemarks: "",
      country: selectedCountry ? selectedCountry.value : "",
    },
    resolver: zodResolver(schema),
  });

  const onSubmit = (data) => {
    if (tripId) {
      TripUpdateMutation.mutate(data);
    }
  };

  const getRouteNameList = (searchString = null, country = null) => {
    getSearchAbleRoutes({ searchString, country })
      .then(async (response) => {
        const fetchedRouteData = response.data;
        let options = [];
        let routeIdArray = [];

        fetchedRouteData.forEach((element) => {
          const option = { value: `${element._id}`, label: `${element.name}` };
          options.push(option);
          routeIdArray.push(element._id);
        });
        // setRouteIdArray(routeIdArray);
        setRouteOptions(options);
        if(options.length == 0) {
          setSelectedRouteId(null);
          setValue("tripRoute", "");
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const getActiveCountries = () => {
    getActiveCountryList({ limit: 100, page: 1, status: true })
      .then((response) => {
        if (response.success && response.data) {
          let options = response.data.map((element) => ({
            value: `${element._id}`,
            label: `${element.name}`,
          }));
          setCountryOptions(options);
        }
      })
      .catch((error) => console.log(error));
  };

  const customFormReset = () => {
    reset();
    setValue("tripId", "");
    setSelectedRouteId("");
    setValue("tripRoute", "");

    setSelectedDriverId("");
    setValue("tripAssignedTo", "");
    setSelectedCountry("");
    setValue("country", "");
    setSelectedTripStatus("");
  };

  const onError = (err) => {
    console.error(err);
  };

  const handleChangeDriver = (selectedOption) => {
    if (selectedOption) {
      setSelectedDriverId(selectedOption);
      setValue("tripAssignedTo", selectedOption.value);
      const driver = allDriverData.find((x) => x._id === selectedOption.value);

      // if (driver && driver.vehicleId) {
      //   setValue("tripVehicleId", driver.vehicleId);
      // }
      if (driver && driver.tagId) {
        setValue("tripTagId", driver.tagId);
      }
    } else {
      setSelectedDriverId("");
      setValue("tripRtripAssignedTooute", "");
      // setValue("tripVehicleId", "");
      setValue("tripTagId", "");
    }
  };

  const handleOnInputChangeDriver = (selectedOption) => {
    if (selectedOption) {
      const expectedData = driverIdArray.find((a) =>
        a.includes(selectedOption)
      );
      if (!expectedData) {
        getSearchedUserList(selectedOption);
      }
    }
  };

  const getSearchedUserList = (value = null, country = null) => {
    const params = {
      limit: 10,
      page: 1,
      type: "driver",
      availibility: true,
    };

    if (value) params.name = value;
    if (country) params.country = country;

    getSearchedUser(params)
      .then(async (response) => {
        const fetchedDriverData = response.data;
        setAllDriverData(fetchedDriverData);
        let options = [];
        let driverIdArray = [];

        fetchedDriverData.forEach((element) => {
          const option = {
            value: `${element._id}`,
            label: `${
              element.firstName +
              " " +
              element.lastName +
              "-" +
              element.employeeCode
            }`,
          };
          options.push(option);
          driverIdArray.push(element._id);
        });
        setDriverIdArray(driverIdArray);
        setDriverOptions(options);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleCountryChange = (selectedOption) => {
    if (selectedOption) {
      setSelectedCountry(selectedOption);
      setValue("country", selectedOption.value);
      getSearchedUserList(null, selectedOption.value);
      // getBookingOrderList(null, selectedOption.value);
      getRouteNameList(null, selectedOption.value);
    } else {
      setSelectedCountry(null);
      setValue("country", "");
      getSearchedUserList();
      // getBookingOrderList();
      getRouteNameList();
    }
  };

  const handleChangeRoute = (selectedOption) => {
    if (selectedOption) {
      setSelectedRouteId(selectedOption);
      setValue("tripRoute", selectedOption.value);
    } else {
      setSelectedRouteId("");
      setValue("tripRoute", "");
    }
  };

  const handleOnInputChangeRoute = (searchValue) => {
    const matchingRoutes = routeOptions.filter((route) =>
      route.label.toLowerCase().includes(searchValue.toLowerCase())
    );
    if (matchingRoutes.length <= 0) {
      getRouteNameList(searchValue);
    }
  };

  useEffect(() => {
    if (tripId && !tripData && tripLoading && !tripError) {
      toast.loading("Getting trip data...");
    }

    if (tripId && tripData && tripData.success && !tripLoading && !tripError) {
      toast.dismiss();
      const fetchedTripData = tripData.data;
      console.log(fetchedTripData);

      setValue("tripName", fetchedTripData.tripName);
      setValue("tripId", fetchedTripData.tripId);
      setValue("orderIds", fetchedTripData.orderIds);
      setValue("bookingOrderId", fetchedTripData.bookingOrderId);
      setValue("tripRoute", fetchedTripData.tripRoute);
      setValue("tripStartTime", fetchedTripData.tripStartTime);
      setValue("tripAssignedBy", fetchedTripData.tripAssignedBy?._id);
      setValue("tripAssignedTo", fetchedTripData.tripAssignedTo);
      setValue("tripRemarks", fetchedTripData.tripRemarks);
      setValue("priority", fetchedTripData.priority);
      setSelectedTripStatus(fetchedTripData.tripStatus);
      if (fetchedTripData.country) {
        const country = countryOptions.find(
          (x) => x.value === fetchedTripData.country
        );
        setSelectedCountry(country);

        setValue("country", fetchedTripData.country);
      }

      if (fetchedTripData.tripRoute) {
        setSelectedRouteId({
          value: fetchedTripData.tripRoute._id,
          label: fetchedTripData.tripRoute.name,
        });
        setValue("tripRoute", fetchedTripData.tripRoute._id);
      }

      if (fetchedTripData.tripAssignedTo) {
        setSelectedDriverId({
          value: fetchedTripData.tripAssignedTo._id,
          label:
            fetchedTripData.tripAssignedTo.firstName +
            " " +
            fetchedTripData.tripAssignedTo.lastName,
        });
        setValue("tripAssignedTo", fetchedTripData.tripAssignedTo._id);
        // setValue("tripVehicleId", fetchedTripData.tripVehicleId);
        setValue("tripTagId", fetchedTripData.tripTagId);
      }

      setIsFormReady(true);
    }
  }, [tripId, tripData, tripLoading, tripError]);

  useEffect(() => {
    if (user.userType === "superAdmin" && countryOptions.length > 0) {
      const fetchedCountry = getValues("country");
      const country = countryOptions.find((x) => x.value === fetchedCountry);
      setSelectedCountry(country);
    }
  }, [countryOptions]);

  useEffect(() => {
    if (user) setValue("tripAssignedBy", user.id);

    if (user?.country) {
      setValue("country", user.country._id);
      setSelectedCountry({ value: user.country._id, label: user.country.name });
    }

    if (user && user.userType === "superAdmin") getActiveCountries();

    getRouteNameList();
    getSearchedUserList();
  }, []);

  return (
    <>
      {isFormReady && (
        <CCard className="mb-4">
          <CCardBody>
            <CForm onSubmit={handleSubmit(onSubmit, onError)}>
              <CRow>
                <CCol sm={6}>
                  {user && user.userType === "superAdmin" && (
                    <CRow className="mb-3">
                      <CFormLabel className="col-sm-5">Country:</CFormLabel>
                      <CCol sm={7}>
                        <Select
                          value={selectedCountry}
                          onChange={handleCountryChange}
                          options={countryOptions}
                          isClearable={true}
                          isDisabled={user.userType !== "superAdmin"}
                        />
                        {errors.country?.message && (
                          <p className="error_text">
                            {errors.country?.message}
                          </p>
                        )}
                      </CCol>
                    </CRow>
                  )}
                  <CRow className="mb-3">
                    <CCol sm={5}>
                      <CFormLabel
                        htmlFor="tripId"
                        className="col-sm-3 col-form-label w-100"
                      >
                        Trip Name:
                      </CFormLabel>
                    </CCol>

                    <CCol sm={7}>
                      <CFormInput
                        type="text"
                        id="tripName"
                        {...register("tripName")}
                      />
                      {errors.tripName?.message && (
                        <p className="error_text">{errors.tripName?.message}</p>
                      )}
                    </CCol>
                  </CRow>
                  <CRow className="mb-3">
                    <CCol sm={5}>
                      <CFormLabel
                        htmlFor="tripId"
                        className="col-sm-3 col-form-label w-100"
                      >
                        Trip ID:
                      </CFormLabel>
                    </CCol>

                    <CCol sm={7}>
                      <CFormInput
                        type="text"
                        id="tripId"
                        {...register("tripId")}
                        disabled={true}
                      />
                      {errors.tripId?.message && (
                        <p className="error_text">{errors.tripId?.message}</p>
                      )}
                    </CCol>
                  </CRow>

                  <CRow className="mb-3">
                    <CFormLabel
                      htmlFor="trip"
                      className="col-sm-5 col-form-label"
                    >
                      Route Name:
                    </CFormLabel>
                    <CCol sm={7}>
                      <Select
                        value={selectedRouteId}
                        onChange={handleChangeRoute}
                        onInputChange={handleOnInputChangeRoute}
                        options={routeOptions}
                        isClearable={true}
                        // isDisabled={true}
                      />
                      {errors.tripRoute?.message && (
                        <p className="error_text">{errors.tripRoute?.message}</p>
                      )}
                      {/* <input
                        type="text"
                        disabled
                        value={selectedRouteId?.label}
                      />
                      {errors.tripRoute?.message && (
                        <p className="error_text">
                          {errors.tripRoute?.message}
                        </p>
                      )} */}
                    </CCol>
                  </CRow>

                  <CRow className="mb-3">
                    <CFormLabel
                      htmlFor="tripStartTime"
                      className="col-sm-5 col-form-label"
                    >
                      Start Time:
                    </CFormLabel>
                    <CCol sm={7}>
                      <CFormInput
                        type="time"
                        id="tripStartTime"
                        {...register("tripStartTime")}
                      />
                      {errors.tripStartTime?.message && (
                        <p className="error_text">
                          {errors.tripStartTime?.message}
                        </p>
                      )}
                    </CCol>
                  </CRow>
                  <CRow className="mb-3">
                    <CFormLabel
                      htmlFor="tripAssignedBy"
                      className="col-sm-5 col-form-label"
                    >
                      CVIT Officer:
                    </CFormLabel>
                    <CCol sm={7}>
                      <CFormInput
                        disabled
                        type="text"
                        id="tripAssignedBy"
                        value={user.firstName + " " + user.lastName}
                      />
                    </CCol>
                  </CRow>
                </CCol>

                <CCol sm={6}>
                  <CRow className="mb-3">
                    <CFormLabel
                      htmlFor="tripAssignedTo"
                      className="col-sm-4 col-form-label"
                    >
                      Driver:
                    </CFormLabel>
                    <CCol sm={7}>
                      <Select
                        onInputChange={handleOnInputChangeDriver}
                        value={selectedDriverId}
                        onChange={handleChangeDriver}
                        options={driverOptions}
                        isClearable={true}
                        isDisabled={
                          !selectedCountry && selectedTripStatus !== "assigned"
                        }
                      />
                      {errors.tripAssignedTo?.message && (
                        <p className="error_text">
                          {errors.tripAssignedTo?.message}
                        </p>
                      )}
                    </CCol>
                  </CRow>
                  <CRow className="mb-3">
                    {/* <CFormLabel
                      htmlFor="tripVehicleId"
                      className="col-sm-4 col-form-label"
                    >
                      Vehicle ID:
                    </CFormLabel> */}
                    <CFormLabel
                      htmlFor="tripTagId"
                      className="col-sm-4 col-form-label"
                    >
                      Tag ID:
                    </CFormLabel>
                    <CCol sm={7}>
                      <CFormInput
                        type="text"
                        id="tripTagId"
                        {...register("tripTagId")}
                        disabled={true}
                      />
                      {errors.tripTagId?.message && (
                        <p className="error_text">
                          {errors.tripTagId?.message}
                        </p>
                      )}
                    </CCol>
                  </CRow>
                  <CRow className="mb-3">
                    <CFormLabel
                      htmlFor="tripRemarks"
                      className="col-sm-4 col-form-label"
                    >
                      Remarks:
                    </CFormLabel>
                    <CCol sm={7}>
                      <CFormTextarea
                        id="tripRemarks"
                        rows={4}
                        {...register("tripRemarks")}
                      ></CFormTextarea>
                      {errors.tripRemarks?.message && (
                        <p className="error_text">
                          {errors.tripRemarks?.message}
                        </p>
                      )}
                    </CCol>
                  </CRow>
                </CCol>

                <div className="d-flex justify-content-end mt-3">
                  <CButton type="submit" color="primary" className="px-5 py-2">
                    Submit
                  </CButton>
                </div>
              </CRow>
            </CForm>
          </CCardBody>
        </CCard>
      )}
    </>
  );
};

export default UpdateTrip;
